import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  total: number;
  limit: number;
  offset: number;
  onPageChange: (newOffset: number) => void;
  onLimitChange?: (newLimit: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  total,
  limit,
  offset,
  onPageChange,
  onLimitChange,
}) => {
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  // Show pagination controls if there's data or if user can change limit
  if (total === 0) return null;

  const pages = [];
  const maxVisiblePages = 5;

  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  const handleLimitChange = (newLimit: number) => {
    if (onLimitChange) {
      onLimitChange(newLimit);
      // Reset to first page when changing limit
      onPageChange(0);
    }
  };

  return (
    <div className="flex flex-col gap-4 px-4 py-4 sm:px-6 mt-4">
      {/* Items per page selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label htmlFor="items-per-page" className="text-sm text-slate-600 dark:text-slate-400">
            Items por página:
          </label>
          <select
            id="items-per-page"
            value={limit}
            onChange={(e) => handleLimitChange(Number(e.target.value))}
            className="input-modern py-1 px-2 text-sm w-auto"
            disabled={!onLimitChange}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Mostrando <span className="font-semibold text-slate-900 dark:text-white">{offset + 1}</span> a{' '}
            <span className="font-semibold text-slate-900 dark:text-white">{Math.min(offset + limit, total)}</span>{' '}
            de <span className="font-semibold text-slate-900 dark:text-white">{total}</span> resultados
          </p>
        </div>
      </div>

      {/* Navigation controls - only show if there's more than one page */}
      {totalPages > 1 && (
        <>
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => onPageChange(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="btn-base btn-secondary py-2 px-4 disabled:opacity-50"
            >
              Anterior
            </button>
            <button
              onClick={() => onPageChange(offset + limit)}
              disabled={offset + limit >= total}
              className="btn-base btn-secondary py-2 px-4 ml-3 disabled:opacity-50"
            >
              Próximo
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-center">
            <div>
              <nav
                className="isolate inline-flex -space-x-px rounded-xl shadow-sm bg-white dark:bg-slate-900"
                aria-label="Pagination"
              >
                <button
                  onClick={() => onPageChange(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="relative inline-flex items-center rounded-l-xl px-3 py-2 text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <span className="sr-only">Anterior</span>
                  <ChevronLeft className="h-5 w-5" />
                </button>

                {startPage > 1 && (
                  <>
                    <button
                      onClick={() => onPageChange(0)}
                      className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-900 dark:text-slate-200 border-y border-r border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 focus:z-20 focus:outline-offset-0"
                    >
                      1
                    </button>
                    {startPage > 2 && (
                      <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-500 border-y border-r border-slate-200 dark:border-slate-700 focus:outline-offset-0">
                        ...
                      </span>
                    )}
                  </>
                )}

                {pages.map((page) => (
                  <button
                    key={page}
                    onClick={() => onPageChange((page - 1) * limit)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold border-y border-r border-slate-200 dark:border-slate-700 ${
                      page === currentPage
                        ? 'z-10 bg-primary-600 text-white border-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                        : 'text-slate-900 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 focus:z-20 focus:outline-offset-0'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                {endPage < totalPages && (
                  <>
                    {endPage < totalPages - 1 && (
                      <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-500 border-y border-r border-slate-200 dark:border-slate-700 focus:outline-offset-0">
                        ...
                      </span>
                    )}
                    <button
                      onClick={() => onPageChange((totalPages - 1) * limit)}
                      className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-slate-900 dark:text-slate-200 border-y border-r border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 focus:z-20 focus:outline-offset-0"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() => onPageChange(offset + limit)}
                  disabled={offset + limit >= total}
                  className="relative inline-flex items-center rounded-r-xl px-3 py-2 text-slate-400 border-y border-r border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <span className="sr-only">Próximo</span>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
